import { useTranslation } from '@hopin-team/provider-translation';
import React, { useCallback, useRef } from 'react';

import {
  type TimelineZoomScale,
  TIMELINE_ZOOM_SCALES,
} from './edit-view-player-context';

type ZoomSliderProps = {
  value: TimelineZoomScale;
  onChange?: (nextScale: TimelineZoomScale) => void;
  ariaLabel: string;
};

const ZOOM_SCALE_MIN = TIMELINE_ZOOM_SCALES[0];
const ZOOM_SCALE_MAX = TIMELINE_ZOOM_SCALES[TIMELINE_ZOOM_SCALES.length - 1];
const ZOOM_SLIDER_TRACK_WIDTH_PX = 60;
const ZOOM_SLIDER_THUMB_SIZE_PX = 20;
const ZOOM_SLIDER_WRAPPER_WIDTH_PX =
  ZOOM_SLIDER_TRACK_WIDTH_PX + ZOOM_SLIDER_THUMB_SIZE_PX;

const clampZoomScale = (value: number): TimelineZoomScale => {
  const boundedValue = Math.min(
    ZOOM_SCALE_MAX,
    Math.max(ZOOM_SCALE_MIN, Math.round(value)),
  );
  return boundedValue as TimelineZoomScale;
};

export const ZoomSlider = ({
  value,
  onChange,
  ariaLabel,
}: ZoomSliderProps): React.JSX.Element => {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLDivElement | null>(null);

  const handleTrackPosition = useCallback(
    (clientX: number) => {
      if (!Number.isFinite(clientX)) {
        return;
      }
      const track = trackRef.current;
      if (!track) return;

      const rect = track.getBoundingClientRect();
      if (rect.width === 0) return;

      const offsetX = Math.min(rect.width, Math.max(0, clientX - rect.left));
      const ratio = offsetX / rect.width;
      const nextValue = clampZoomScale(
        ZOOM_SCALE_MIN + ratio * (ZOOM_SCALE_MAX - ZOOM_SCALE_MIN),
      );
      onChange?.(nextValue);
    },
    [onChange],
  );

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      handleTrackPosition(event.clientX);
    },
    [handleTrackPosition],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (
        event.currentTarget.hasPointerCapture &&
        !event.currentTarget.hasPointerCapture(event.pointerId)
      ) {
        return;
      }
      handleTrackPosition(event.clientX);
    },
    [handleTrackPosition],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
        event.preventDefault();
        onChange?.(clampZoomScale(value - 1));
        return;
      }
      if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
        event.preventDefault();
        onChange?.(clampZoomScale(value + 1));
        return;
      }
      if (event.key === 'Home') {
        event.preventDefault();
        onChange?.(ZOOM_SCALE_MIN);
        return;
      }
      if (event.key === 'End') {
        event.preventDefault();
        onChange?.(ZOOM_SCALE_MAX);
      }
    },
    [onChange, value],
  );

  const percentage =
    ((value - ZOOM_SCALE_MIN) / (ZOOM_SCALE_MAX - ZOOM_SCALE_MIN)) * 100;

  return (
    <div
      className="relative touch-none"
      style={{ width: `${ZOOM_SLIDER_WRAPPER_WIDTH_PX}px`, height: '20px' }}
      role="slider"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-valuemin={ZOOM_SCALE_MIN}
      aria-valuemax={ZOOM_SCALE_MAX}
      aria-valuenow={value}
      aria-valuetext={t('content-recording.editor-v2.zoom-step', {
        value,
      })}
      onKeyDown={handleKeyDown}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      data-testid="zoom-slider"
    >
      <div
        ref={trackRef}
        className="absolute top-1/2 -translate-y-1/2 rounded-full bg-spring-neutral-b4"
        style={{
          left: `${ZOOM_SLIDER_THUMB_SIZE_PX / 2}px`,
          width: `${ZOOM_SLIDER_TRACK_WIDTH_PX}px`,
          height: '4px',
        }}
      >
        <div
          className="h-full rounded-full bg-spring-neutral-b3"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div
        className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-spring-neutral-b3 shadow-sm"
        style={{
          left: `calc(${
            ZOOM_SLIDER_THUMB_SIZE_PX / 2
          }px + (${percentage} * ${ZOOM_SLIDER_TRACK_WIDTH_PX}px / 100))`,
          width: `${ZOOM_SLIDER_THUMB_SIZE_PX}px`,
          height: `${ZOOM_SLIDER_THUMB_SIZE_PX}px`,
        }}
      />
    </div>
  );
};
